import { api, API_BASE, ApiError } from './client';

// "Suggested Ledger" — a categorization guess, not a real matched Payment
// (matchStatus/matchedPaymentId below is that). See CLAUDE.md's
// bank-statement-parsing note for the full story.
// 'purchase' is the debit-side mirror of 'party' — a debit paid to a vendor,
// resolving to the same real partyId FK as 'party' (credit, a customer).
// 'transfer' (a self/inter-account transfer) and 'other' (a label-only
// Loan/Capital/Asset movement, see ledger-suggestion.ts's OTHER_LABELS)
// never create or link any real record.
export type LedgerType = 'expense' | 'party' | 'purchase' | 'transfer' | 'other';

export interface BankStatementEntry {
  id: string;
  statementId: string;
  date: string;
  description: string;
  debit?: number;
  credit?: number;
  balance?: number;
  reference?: string;
  matchedPaymentId?: string;
  matchStatus: 'matched' | 'unmatched' | 'ignored';
  suggestedLabel?: string;
  suggestedType?: LedgerType;
  categoryConfirmed: boolean;
  // Set only when suggestedType/type is 'party' (a credit, a customer) or
  // 'purchase' (a debit, a vendor) — the real linked Party's id (see
  // BankStatementEntry.partyId's own schema comment). Populated either by
  // the upload-time suggestion (an unconfirmed guess) or by the user
  // explicitly picking a party via PartyLedgerPicker (confirmed).
  partyId?: string;
  // Set only when suggestedType/type is 'expense' and the category has been
  // confirmed — the real, genuinely-booked Expense this entry created (or is
  // kept in sync with) on confirm. See BankStatementEntry.expenseId's own
  // schema comment. The UI doesn't read this directly (categoryConfirmed's
  // green border already communicates "saved") but it's here for
  // completeness/debugging.
  expenseId?: string;
}

export interface ReconciliationResult {
  checked: boolean;
  passed: boolean;
  issues: string[];
}

export interface BankStatement {
  id: string;
  tenantId: string;
  bankName: string;
  accountNumber?: string;
  uploadedAt: string;
  fromDate: string;
  toDate: string;
  closingBalance?: number;
  entries?: BankStatementEntry[];
  _count?: { entries: number };
  // Set by listStatements() only (computed server-side, not persisted on
  // the statement itself) — powers the Banking page's Total/Pending/Saved/
  // Suggestion columns.
  suggestionCount?: number;
  confirmedCount?: number;
  pendingCount?: number;
}

// Mirrors apps/api/src/bank-reconciliation/column-roles.ts's ColumnRole —
// kept in sync by hand (this repo doesn't share types across the API/web
// boundary at build time, see CLAUDE.md's "Shared packages" note — this
// isn't one of the workspace packages that IS shared that way).
export type ColumnRole =
  | 'serial' | 'tranId' | 'date' | 'valueDate' | 'narration' | 'reference'
  | 'debit' | 'credit' | 'amount' | 'drcr' | 'balance' | 'other';

export interface ColumnOverride {
  index: number;
  role: ColumnRole;
}

export interface ColumnInfo {
  index: number;
  label: string;
  role: ColumnRole;
  sample: string[];
}

export interface PreviewEntry {
  date: string;
  description: string;
  debit?: number;
  credit?: number;
  balance?: number;
  reference?: string;
}

export interface ParsePreviewResult {
  bankName: string;
  columns: ColumnInfo[];
  appliedMapping: ColumnOverride[];
  sampleRows: PreviewEntry[];
  entryCount: number;
  skippedRows: number;
  // The OR of two independent server-side checks — assessNarrationQuality()
  // (a shape check on the narration text that *was* extracted) and
  // assessRowLoss() (an aggregate check on how much was silently dropped as
  // unparseable, added for TRACKER.md R35: a real statement whose row-
  // banding misfired badly enough that only 28 of 68 real transactions came
  // through, every one of the 28 individually looking perfectly valid, so a
  // narration-shape check alone had nothing to catch).
  parseSuspect: boolean;
  parseSuspectReason?: string;
  // True only when the API's ENABLE_PDF_OCR fallback actually read this
  // statement via Google Cloud Vision (no digital text layer at all —
  // a scanned/photographed statement). See CLAUDE.md's bank-statement-
  // parsing note. Forces the mapping-review modal open regardless of
  // parseSuspect, since an OCR digit misread (0/O, 1/I, 5/S) can look
  // like a perfectly plausible amount.
  ocrUsed: boolean;
}

function buildForm(file: File, password?: string, columnOverrides?: ColumnOverride[]): FormData {
  const form = new FormData();
  form.append('file', file);
  if (password) form.append('password', password);
  if (columnOverrides && columnOverrides.length > 0) form.append('columnOverrides', JSON.stringify(columnOverrides));
  return form;
}

async function postForm<T>(path: string, form: FormData): Promise<T> {
  const token = sessionStorage.getItem('cl_token');
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    const body = (errText ? JSON.parse(errText) : {}) as { message?: string; code?: string };
    // A real ApiError (not a plain Error) so callers can branch on `code` —
    // e.g. ReportsPage.tsx detecting PDF_PASSWORD_REQUIRED to show a
    // password prompt instead of a generic failure message.
    throw new ApiError(res.status, body.message ?? `Request failed (${res.status})`, undefined, body.code);
  }
  return res.json();
}

async function uploadFile(
  file: File,
  bankName?: string,
  accountNumber?: string,
  password?: string,
  columnOverrides?: ColumnOverride[],
  // True only when columnOverrides came from a human actually reviewing
  // BankStatementMappingModal — never from doUpload()'s unreviewed
  // "confidently auto-detected" fast path, which resubmits the server's own
  // preview.appliedMapping as if it were a correction. Lets the server
  // cache a mapping it knows a person actually looked at (BankFormatProfile
  // — see CLAUDE.md's Bank Reconciliation note) without ever caching one
  // nobody reviewed.
  confirmedByUser = false,
): Promise<{ statementId: string; entryCount: number; bankName: string; skippedRows: number; closingBalance?: number; verification: ReconciliationResult; ocrUsed: boolean }> {
  const form = buildForm(file, password, columnOverrides);
  if (bankName) form.append('bankName', bankName);
  if (accountNumber) form.append('accountNumber', accountNumber);
  if (confirmedByUser) form.append('confirmedByUser', 'true');
  return postForm('/bank-recon/upload', form);
}

// Parses a file without persisting it — no statement/entries are created.
// Used both for the fast path (preview, then immediately commit with the
// exact mapping preview already computed, when nothing looks uncertain) and
// for the mapping-review modal (re-called live as the user adjusts a
// column's role, so the sample-rows table always reflects the server's own
// parsing logic rather than a duplicated copy of it in frontend TypeScript).
function previewUpload(file: File, password?: string, columnOverrides?: ColumnOverride[]): Promise<ParsePreviewResult> {
  const form = buildForm(file, password, columnOverrides);
  return postForm('/bank-recon/upload/preview', form);
}

export const bankReconApi = {
  upload: uploadFile,
  previewUpload,
  listStatements: () => api.get<BankStatement[]>('/bank-recon/statements'),
  getStatement: (id: string) => api.get<BankStatement>(`/bank-recon/statements/${id}`),
  autoMatch: (id: string) => api.post<{ matched: number; unmatched: number }>(`/bank-recon/statements/${id}/auto-match`, {}),
  manualMatch: (entryId: string, paymentId: string) =>
    api.post<BankStatementEntry>(`/bank-recon/entries/${entryId}/match`, { paymentId }),
  unmatch: (entryId: string) =>
    api.post<BankStatementEntry>(`/bank-recon/entries/${entryId}/unmatch`, {}),
  // `skipped` counts entries left untouched: an already-Auto-Matched entry
  // (would double-count a real Payment as an Expense too) or, for
  // 'expense', a row with no debit amount (a credit row caught up in a
  // mixed bulk selection).
  bulkAssignLedger: (entryIds: string[], label: string, type: LedgerType, partyId?: string, loanAccountId?: string) =>
    api.patch<{ updated: number; skipped: number }>('/bank-recon/entries/bulk', { entryIds, label, type, partyId, loanAccountId }),
  deleteStatement: (id: string) =>
    api.delete<{ deleted: boolean }>(`/bank-recon/statements/${id}`),
};
